import { useEffect } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { RouterProvider } from '@tanstack/react-router'
import { ApiError, OfflineError } from '@mads/shared'
import { Logo } from '@/components/Logo'
import { SyncBlocker } from '@/components/SyncBlocker'
import { useIsOnline } from '@/lib/connectivity'
import { forgetOfflineCache, OFFLINE_CACHE_MAX_AGE, persistOptions } from '@/lib/offlineCache'
import { useQueueStore } from '@/lib/queue'
import { restoreSession, useSessionStore } from '@/lib/session'
import { SNAPSHOT_INTERVAL, useSnapshotStore } from '@/lib/snapshot'
import { useIsBlockedBySync, useSyncStore } from '@/lib/sync'
import { router } from '@/router'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Long enough to outlive the persisted copy it is written from (issue
      // #64). A query the cache has garbage-collected is not in the next
      // dehydration either, so the default five minutes would quietly empty the
      // cellar of every screen nobody had visited lately.
      gcTime: OFFLINE_CACHE_MAX_AGE,
      retry: (failureCount, error) => {
        // A connection that is not there will not be there a second later
        // either; the query pauses and picks itself up when the network comes
        // back. Checked first because an `OfflineError` is an `ApiError`.
        if (error instanceof OfflineError) return false

        // A rejected token is not worth retrying either. Anything else gets one.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return false
        }

        return failureCount < 1
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Attempt and fail rather than pause and resume (issue #64). TanStack's
      // default holds an offline mutation and replays it on reconnect, which is
      // a queue with no client ids, no base values and no conflict resolution —
      // the very thing this app must not do behind somebody's back. Until the
      // real queue exists, a write made in a cellar says so.
      networkMode: 'always',
    },
  },
})

/**
 * Verifies a token restored from storage before the app renders. Without this
 * an expired token would show the shell for a moment, then bounce.
 *
 * `verified` rather than "is there a session", because a session now arrives
 * from storage as well as from a sign-in (issue #64) and only one of those is
 * evidence the token still works. It stays false when the check could not be
 * made at all, which is what puts `online` in the dependencies: coming back
 * into signal is the moment to ask again.
 */
function useRestoreSession() {
  const token = useSessionStore((s) => s.token)
  const verified = useSessionStore((s) => s.verified)
  const setLoading = useSessionStore((s) => s.setLoading)
  const online = useIsOnline()

  useEffect(() => {
    if (token === null || verified) {
      setLoading(false)
      return
    }

    void restoreSession()
  }, [token, verified, setLoading, online])
}

function SplashScreen() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Logo layout="stacked" theme="white" size={52} />
    </div>
  )
}

/**
 * The router evaluates the session guard in `beforeLoad`, which only runs on
 * navigation. Signing out — from the button, or from the API client on a
 * rejected token — changes the store without navigating, so the redirect has to
 * be driven from here.
 *
 * Signing out also empties the caches, both the one in memory and the device's
 * copy of it (issue #64). Without that, the next person to sign in on the same
 * tablet reads the previous walk's inventory until every query has refetched.
 */
function useRedirectOnSignOut() {
  useEffect(
    () =>
      useSessionStore.subscribe((state, previous) => {
        if (previous.token !== null && state.token === null) {
          queryClient.clear()
          void forgetOfflineCache()
          // The copy is going, so the date on it goes too — otherwise the next
          // person to sign in on this tablet is told there is an hour-old
          // inventory saved, over caches that were just emptied.
          void useSnapshotStore.getState().forget()
          void router.navigate({ to: '/sign-in' })
        }
      }),
    [],
  )
}

/**
 * Loads this account's queue, and replays it the moment there is a connection
 * to replay it over (issue #64, step 2).
 *
 * Keyed on the user id because the queue is stored against the account that
 * made the changes: `JWT_TTL` is twelve hours with no refresh token, so a
 * device offline longer than that comes back holding a queue and a dead token,
 * and the queue has to survive signing out and back in. It must equally never
 * be replayed under somebody else's name, which is why the id is part of the
 * key rather than something checked at sync time and hoped for.
 */
function useQueue() {
  const userId = useSessionStore((s) => s.session?.user.id ?? null)
  const loaded = useQueueStore((s) => s.userId)
  const load = useQueueStore((s) => s.load)
  const depth = useQueueStore((s) => s.changesets.length)
  const run = useSyncStore((s) => s.run)
  const online = useIsOnline()

  useEffect(() => {
    if (userId !== null && userId !== loaded) void load(userId)
  }, [userId, loaded, load])

  useEffect(() => {
    if (online && userId !== null && userId === loaded && depth > 0) {
      void run(queryClient)
    }
  }, [online, userId, loaded, depth, run])
}

/**
 * Brings the whole inventory down to the device, and keeps doing it (issue #70).
 *
 * Three moments, and they are the three that matter: when a session is known —
 * so a tablet signed into at a desk is ready before anybody carries it
 * downstairs — when the connection comes back, and every quarter of an hour
 * while the app is open. `refresh` itself decides whether now is a good time;
 * in particular it stands down while the queue has anything in it, because a
 * refresh then would replace the walker's own unsent changes with the server's
 * copy of the records they had changed.
 */
function useSnapshot() {
  const userId = useSessionStore((s) => s.session?.user.id ?? null)
  const queueLoaded = useQueueStore((s) => s.userId)
  // Whether the queue is empty, rather than how deep it is: a refresh stands
  // down while anything is waiting, so forty counts made in a cellar would
  // otherwise restart the interval forty times and it would never fire.
  const idle = useQueueStore((s) => s.changesets.length === 0)
  const refresh = useSnapshotStore((s) => s.refresh)
  const load = useSnapshotStore((s) => s.load)
  const online = useIsOnline()

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    // Not before the queue has been read back: an empty queue and a queue
    // nobody has looked at yet are indistinguishable from here, and refreshing
    // over the second would overwrite changes that are about to be loaded.
    if (userId === null || userId !== queueLoaded || !online) return

    void refresh(queryClient)

    const timer = setInterval(() => void refresh(queryClient), SNAPSHOT_INTERVAL)

    return () => clearInterval(timer)
    // A queue that has just drained is exactly when the device is both allowed
    // to refresh and most out of date, so `idle` belongs in here.
  }, [userId, queueLoaded, online, idle, refresh])
}

function Shell() {
  useRestoreSession()
  useRedirectOnSignOut()
  useQueue()
  useSnapshot()
  const loading = useSessionStore((s) => s.loading)
  const blocked = useIsBlockedBySync()

  // The router reads the token in beforeLoad, so it must not run until the
  // restored session has been verified.
  if (loading) return <SplashScreen />

  return (
    <>
      <RouterProvider router={router} />
      {/*
        In the same position in the tree as the splash, and over everything:
        while a queue is being replayed or argued about, nothing else in the
        app is reachable. A half-synced inventory is a worse thing to browse
        than none.
      */}
      {blocked && <SyncBlocker />}
    </>
  )
}

export function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <Shell />
    </PersistQueryClientProvider>
  )
}
